import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useNavigate } from "#routers/hooks";
import { EditRoleFormPanel } from "#src/batteries-included-components/Panels/DetailsPanel/EditRoleFormPanel";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import {
  RoleContext,
  RoleProvider,
} from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail/RoleContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FilterBuilderFieldsType,
  Form,
  FormButton,
  Page,
  useForm,
  useToast,
} from "@validereinc/common-components";
import { AssetType, RoleUpdateType, RolesAdapter } from "@validereinc/domain";
import { isEmpty } from "es-toolkit/compat";
import React, { useContext, useState } from "react";
import { EDIT_ROLE_BREADCRUMB } from ".";
import { ROLES_BREADCRUMB } from "../..";
import { ROLES_AND_PERMISSIONS_BREADCRUMB } from "../../..";
import { SETTINGS_BREADCRUMB } from "../../../..";
import { ROLE_DETAILS_BREADCRUMB, linkToRoleDetailPage } from "../detail";

const EditRolePageContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { localize } = useLocalization();
  const { roleQuery, rolePermissionsQuery } = useContext(RoleContext) ?? {};
  const facilitiesReadFilter = rolePermissionsQuery?.data?.find(
    (permission) => permission.name === "facilities:read"
  )?.filter;
  const [filter, setFilter] = useState(facilitiesReadFilter ?? {});
  const form = useForm({
    defaultValues: {
      ...roleQuery?.data,
      hasFacilitiesReadFilter: !isEmpty(facilitiesReadFilter),
    },
  });
  const breadcrumbs = useBreadcrumbs(
    [
      SETTINGS_BREADCRUMB,
      ROLES_AND_PERMISSIONS_BREADCRUMB,
      ROLES_BREADCRUMB,
      ROLE_DETAILS_BREADCRUMB,
      EDIT_ROLE_BREADCRUMB,
    ],
    {
      3: roleQuery?.data?.name,
    },
    { roleId: roleQuery?.data?.id ?? "" }
  );
  const queryClient = useQueryClient();

  const { customAttributeFilterBuilderFieldDefinitions } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.FACILITY,
    });

  const fields: FilterBuilderFieldsType = {
    name: {
      data_type: "string",
      display_name: `${localize("Facility")} Name`,
    },
    ...customAttributeFilterBuilderFieldDefinitions,
  };

  const navigateToRoleDetail = () => {
    if (!roleQuery?.data?.id) return;

    navigate({ pathname: linkToRoleDetailPage(roleQuery.data.id) });
  };

  const hasFacilitiesReadFilter = form.getValues("hasFacilitiesReadFilter");

  const updateRoleMutation = useMutation({
    mutationFn: async ({ name, description }: RoleUpdateType) => {
      if (!roleQuery?.data?.id) {
        throw new Error("Role ID must be specified to update it");
      }

      await RolesAdapter.updateOne({
        id: roleQuery.data.id,
        data: {
          name,
          description,
        },
      });

      if (hasFacilitiesReadFilter) {
        await RolesAdapter.permissions.update({
          id: roleQuery.data.id,
          data: {
            active: true,
            filter,
          },
          meta: { permissionName: "facilities:read" },
        });
        // If the facility filter checkbox is unchecked,
        // and we intially had a facility filter, delete the permission
      } else if (!isEmpty(facilitiesReadFilter)) {
        await RolesAdapter.permissions.delete({
          id: roleQuery.data.id,
          meta: { permissionName: "facilities:read" },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      toast.push({
        intent: "success",
        description: "Successfully updated role",
      });
      navigateToRoleDetail();
    },
    onError: (err) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Failed to update role",
      });
    },
  });

  const footer = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 16,
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      <Button
        onClick={navigateToRoleDetail}
        disabled={updateRoleMutation.isLoading}
        isLoading={roleQuery?.isLoading}
      >
        Cancel
      </Button>

      <FormButton
        variant="primary"
        type="submit"
        onClick={form.handleSubmit(updateRoleMutation.mutate as any)}
        disabled={roleQuery?.isLoading ?? updateRoleMutation.isLoading}
      >
        Save
      </FormButton>
    </div>
  );

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(updateRoleMutation.mutate as any) as any}
      style={{ height: "100%" }}
    >
      <Page
        title={roleQuery?.data?.name ?? "Role"}
        category={`Edit Role`}
        isLoading={roleQuery?.isLoading}
        breadcrumbs={breadcrumbs}
        footer={footer}
        shouldCenterContent
      >
        <EditRoleFormPanel
          fields={fields}
          filter={filter}
          setFilter={setFilter}
        />
      </Page>
    </Form>
  );
};

export const EditRolePage = () => (
  <RoleProvider>
    <EditRolePageContent />
  </RoleProvider>
);
